import { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Product } from "@made-people/grease-product";
import { useBffClient } from "../../../hooks/use-bff-client";
import { useFrontendContext } from "../../../hooks/use-frontend-context";
import { frontendProductToGreaseProduct } from "../../../services/bff/mappers/frontend-product-to-grease-product";
import { useAllocatedWarehouseIds } from "../../../services/centra/allocation-rule/use-allocated-warehouse-ids";

type QuickShopChildren = (
  products: Product[],
  baseProductId: string,
) => ReactNode;

export const QuickShopWithSiblings = ({
  productIds,
  children,
}: {
  productIds: string[];
  children: QuickShopChildren;
}) => {
  const bffClient = useBffClient();
  const frontendContext = useFrontendContext();
  const allocatedWarehouseIds = useAllocatedWarehouseIds();

  const { data, isError } = useQuery({
    queryKey: ["quickshop", ...productIds, ...(allocatedWarehouseIds ?? [])],
    queryFn: async () => {
      const response = await bffClient.fetchProductWithSiblings({
        frontendContext,
        productIds,
      });

      if (!response || !response.frontendProducts?.length) {
        return null;
      }

      return {
        baseProductId: response.baseProductId,
        products: response.frontendProducts.map((fp) =>
          frontendProductToGreaseProduct(
            fp,
            frontendContext,
            allocatedWarehouseIds,
          ),
        ),
      };
    },
    enabled: productIds.length > 0,
  });

  if (isError) {
    return null;
  }

  if (!data) {
    return <QuickShopSkeleton />;
  }

  return <>{children(data.products, data.baseProductId)}</>;
};

const QuickShopSkeleton = () => (
  <div className="w-full max-w-screen-2xl mx-auto animate-pulse bg-white my-4 lg:my-12">
    <section className="grid grid-cols-12 gap-4 w-full p-10 lg:grid-cols-[1fr_2fr] h-[1002px] lg:h-[800px]"></section>
  </div>
);
